<template>
  <absolutely-layout
    :enable-scroll-event="true"
    :show-footer="false"
    class="h-screen !overflow-hidden"
  >
    <div class="relative w-screen h-[100svh] bg-[#6bc6ef] overflow-hidden">
      <!-- JPG frame (shows immediately, no blue background) -->
      <img
        :src="backgroundFrame"
        alt="Background"
        class="absolute inset-0 w-full h-[101svh] object-cover pointer-events-none"
      />

      <!-- MP4 video background (swapped in when ready) -->
      <video
        v-show="showVideo"
        ref="backgroundVideo"
        :src="backgroundVideo"
        autoplay
        loop
        muted
        playsinline
        preload="auto"
        class="absolute inset-0 w-full h-[101svh] object-cover pointer-events-none"
        @canplay="onVideoReady"
        @error="onVideoError"
        @wheel.prevent
        @touchmove.prevent
        @scroll.prevent
      />

      <!-- Logo/Text Image at Top -->
      <div
        class="absolute flex items-center justify-center left-1/2 transform -translate-x-1/2 top-[4svh] max-w-[435px] max-h-[35svh]"
      >
        <div class="flex-none rotate-[-1.884deg]">
          <img
            :src="logoImage"
            alt="Absolutely Kids Logo"
            class="max-h-[30svh] max-w-[362px] w-auto h-auto object-contain pointer-events-none"
          />
        </div>
      </div>

      <!-- START Button at Bottom -->
      <div
        class="absolute bottom-[6svh] flex flex-col gap-[16px] items-center left-1/2 transform -translate-x-1/2 z-10 w-full"
      >
        <CtaButton
          label="START"
          :block="false"
          class="w-[177px] text-[2rem] font-bold shadow-[0_0_6px_rgba(0,0,0,0.7)]"
          :style="startButtonStyle"
          @click="handleContinue"
        />
      </div>
    </div>
  </absolutely-layout>
</template>

<script>
import CtaButton from '@/components/Absolutely/CtaButton.vue';
import AbsolutelyAccessMixin from '@/mixins/AbsolutelyAccess';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AbsolutelyEntry',
  components: {
    CtaButton,
  },
  mixins: [AbsolutelyAccessMixin],
  data() {
    return {
      // Background assets
      backgroundFrame: '/assets/absolutely/absolutely-landing-bg-frame.webp',
      backgroundVideo: '/assets/absolutely/absolutely-landing-bg.mp4',

      // Background state
      showVideo: false, // Show video when ready
      videoReady: false,

      // Existing data
      isFirstVisit: true,
      logoImage: '/assets/absolutely/absolutely-logo.webp',
      isPreloading: false,
    };
  },
  computed: {
    ...mapGetters('absolutely', ['hasVisitedAbsolutely']),
  },
  methods: {
    ...mapActions('absolutely', ['fetchContent', 'loadProgress']),

    // Prevent scrolling on this page
    preventScroll(e) {
      e.preventDefault();
    },

    async preloadAbsolutelyHomePage() {
      if (this.isPreloading) return; // Already preloading
      this.isPreloading = true;

      try {
        // Preload store data in parallel
        await Promise.allSettled([
          this.fetchContent({ refresh: true }), // Load chapters and steps content
          this.loadProgress(), // Load user progress
        ]);

        // Preload images that the home page will need
        await this.preloadHomePageImages();
      } catch (error) {
        console.error('Preloading failed (non-critical):', error);
      } finally {
        this.isPreloading = false;
      }
    },
    async preloadHomePageImages() {
      // Preload homepage background images
      const homepageImages = [
        '/assets/absolutely/homepage/homepage1.webp',
        '/assets/absolutely/homepage/homepage2.webp',
        '/assets/absolutely/homepage/ship.png',
      ];

      // Preload chapter island images (we'll preload first few chapters)
      const chapterImages = [];
      for (let i = 1; i <= 5; i++) {
        chapterImages.push(`/assets/absolutely/homepage/island-${i}.webp`);
      }

      // Preload button images
      const buttonImages = [
        '/assets/absolutely/hogc-home-button.png',
        '/assets/absolutely/guide-button.png',
        '/assets/absolutely/menu-button.png',
      ];

      const allImages = [...homepageImages, ...chapterImages, ...buttonImages];

      // Preload all images
      const preloadPromises = allImages.map(src => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = resolve; // Resolve even on error to not block
          img.src = src;
        });
      });

      await Promise.allSettled(preloadPromises);
    },
    startButtonStyle() {
      return {
        textShadow: 'rgba(255, 255, 255, 0.81) -1px 1px 0px',
      };
    },
    handleContinue() {
      // Always go directly to home page (onboarding video removed)
      this.$router.push('/absolutely/home');
    },

    // Preload PNG frame (shows immediately, no blue background)
    preloadPngFrame() {
      return new Promise(resolve => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = () => {
          console.error('Failed to preload PNG frame:', this.backgroundFrame);
          resolve(); // Continue anyway
        };
        img.src = this.backgroundFrame;
      });
    },

    // Start loading MP4 video in background
    startVideoPreload() {
      const video = document.createElement('video');
      video.src = this.backgroundVideo;

      video.oncanplay = () => {
        this.videoReady = true;
        this.showVideo = true; // Swap from PNG to video
      };

      video.onerror = () => {
        // Keep showing PNG if video fails
      };
    },

    // Background loading handlers
    onBackgroundLoaded() {
      this.backgroundLoaded = true;
    },

    onVideoReady() {
      // Video has decoded first frame and is ready to play smoothly
      this.videoReady = true;
      this.showVideo = true;
    },

    onVideoError(error) {
      console.warn('[AbsolutelyEntry] Video error, keeping PNG fallback', error);
      this.showVideo = false; // Keep showing PNG
    },

    onWebPError(error) {
      console.warn('[AbsolutelyEntry] WebP format failed, using GIF fallback', error);
      this.supportsWebP = false;
    },

    // Feature detection
    checkVideoSupport() {
      // Check if browser supports video element and MP4
      // iOS allows autoplay for muted videos with playsinline attribute
      const video = document.createElement('video');
      return video.canPlayType && video.canPlayType('video/mp4; codecs="avc1.42E01E"') !== '';
    },

    async checkWebPSupport() {
      // Check if browser supports animated WebP
      return new Promise(resolve => {
        const webP = new Image();
        webP.onload = webP.onerror = () => {
          const supported = webP.height === 2;
          this.supportsWebP = supported;
          resolve(supported);
        };
        // Test image: 2x2 animated WebP
        webP.src =
          'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
      });
    },
  },
  async mounted() {
    try {
      // Preload JPG frame (shows immediately, no blue background)
      await this.preloadPngFrame();

      // Start video loading in background
      if (this.$refs.backgroundVideo) {
        this.$refs.backgroundVideo.load();
      }

      // Detect format support
      this.supportsVideo = this.checkVideoSupport();
    } catch (error) {
      console.error('Mount error:', error);
    }
  },

  async ionViewWillEnter() {
    // Check access
    if (!(await this.checkAbsolutelyAccess())) return;

    // Check first visit status using store (loaded from backend)
    // Load progress first to get onboarding flags
    await this.loadProgress();
    this.isFirstVisit = !this.hasVisitedAbsolutely;

    // Start preloading absolutely home page data and images in the background
    // This happens while user is viewing the entry page
    this.preloadAbsolutelyHomePage().catch(err => {
      console.warn('Preload error (non-critical):', err);
    });

    // Disable scrolling on this page
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('touchmove', this.preventScroll, { passive: false });
  },

  async ionViewWillLeave() {
    // Re-enable scrolling when leaving this page
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.removeEventListener('touchmove', this.preventScroll);
  },
};
</script>

<style scoped>
:deep(.label) {
  font-size: 42px;
}
</style>
